import { Resource } from "../../utils/types";
import { apiFetch, expectJsonBody } from "../api";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type FetchResourcesParams = {
    page?: number;
    limit?: number;
    search?: string;
};

export type FetchResourcesResult = {
    data: Resource[];
    total: number;
};

export type UnassignedRoomSummaryEntry = {
    resourceId: string;
    resourceName: string;
    count: number;
};

export type UnassignedRoomSummaryResult = {
    data: UnassignedRoomSummaryEntry[];
    /** True when the backend's PostgREST row cap truncated the underlying query — the counts
     * above are a real but incomplete undercount, not a full picture. */
    truncated: boolean;
};

/**
 * Fetch the count of appointments landed in Unassigned because their room is excluded
 * from sync, grouped by room, for the current clinic account.
 * Errors are reported to Sentry by apiFetch and propagated to the caller (React Query).
 */
export const fetchUnassignedRoomSummary = async (): Promise<UnassignedRoomSummaryResult> => {
    const result = await apiFetch<UnassignedRoomSummaryResult>("resources/unassigned-summary", {
        method: "GET",
    });
    return expectJsonBody(result, "GET resources/unassigned-summary");
};

/**
 * Fetch resources for a clinic account with optional pagination and search.
 * Errors are reported to Sentry by apiFetch and propagated to the caller (React Query).
 */
export const fetchResources = async (params: FetchResourcesParams = {}): Promise<FetchResourcesResult> => {
    const searchParams = new URLSearchParams();
    if (params.page != null) searchParams.set("page", String(params.page));
    if (params.limit != null) searchParams.set("pageSize", String(params.limit));
    if (params.search?.trim()) searchParams.set("q", params.search.trim());
    const qs = searchParams.toString();
    const result = await apiFetch<FetchResourcesResult>(`resources${qs ? `?${qs}` : ""}`, { method: "GET" });
    return expectJsonBody(result, "GET resources");
};

/**
 * Update a resource (sync_excluded, is_archived, etc.)
 * Errors are reported to Sentry by apiFetch and propagated to the caller.
 * @param id - Resource ID
 * @param resource - Updated resource fields
 */
export const updateResource = async (id: string, resource: Partial<Resource>): Promise<Resource> => {
    if (!UUID_RE.test(id)) throw new Error("Invalid resource id");
    return expectJsonBody(
        await apiFetch<Resource>(`resources/${id}`, { method: "PATCH", json: resource }),
        `PATCH resources/${id}`,
    );
};

/**
 * Change the sort index of a resource
 * Errors are reported to Sentry by apiFetch and propagated to the caller.
 * @param resourceId - Resource ID
 * @param sortIndex - New sort index
 */
export const changeResourceSortIndex = async (resourceId: string, sortIndex: number): Promise<void> => {
    if (!UUID_RE.test(resourceId)) throw new Error("Invalid resourceId");
    await apiFetch("resources/sortIndex", {
        method: "PATCH",
        json: { resourceId, sortIndex },
    });
};
